// User data caching system to prevent duplicate API calls
import { supabase } from "./supabase";

interface UserCache {
  user: any;
  profile: any;
  role: string | null;
  orgRole: string | null;
  labelTag: string | null;
  timestamp: number;
}

const userCache = new Map<string, UserCache>();
const CACHE_DURATION = 60000; // 1 minute
const pendingRequests = new Map<string, Promise<any>>();

/** A fresh, all-empty cache entry. Single source of the entry shape — spread + override per field. */
const emptyCacheEntry = (): UserCache => ({
  user: null,
  profile: null,
  role: null,
  orgRole: null,
  labelTag: null,
  timestamp: 0,
});

export class UserCacheService {
  private static getCacheKey(
    userId: string,
    type: "user" | "profile" | "role" | "orgRole"
  ): string {
    return `${userId}-${type}`;
  }

  private static isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < CACHE_DURATION;
  }

  static async getUser(userId?: string): Promise<any> {
    // Unified dedupe key so we only ever call auth.getUser once concurrently
    const unifiedKey = "auth-user";

    // If a specific user's data is requested and cached, return from cache
    if (userId) {
      const cacheKey = this.getCacheKey(userId, "user");
      const cached = userCache.get(cacheKey);
      if (cached && this.isCacheValid(cached.timestamp)) {
        return cached.user;
      }
      // If a fetch for this user's data is already pending, return it
      if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
      }
      // Fall through to unified fetch below
    }

    // If a unified auth fetch is in-flight, reuse it
    if (pendingRequests.has(unifiedKey)) {
      const user = await pendingRequests.get(unifiedKey);
      if (!user) return null;
      // Ensure per-user cache is populated
      const cacheKey = this.getCacheKey(user.id, "user");
      userCache.set(cacheKey, { ...emptyCacheEntry(), user, timestamp: Date.now() });
      return user;
    }

    const fetchPromise = (async () => {
      try {
        const {
          data: { user },
          error,
        } = await supabase.auth.getUser();
        if (error) throw error;
        if (!user) return null;

        const cacheKey = this.getCacheKey(user.id, "user");
        userCache.set(cacheKey, { ...emptyCacheEntry(), user, timestamp: Date.now() });

        return user;
      } finally {
        pendingRequests.delete(unifiedKey);
      }
    })();

    pendingRequests.set(unifiedKey, fetchPromise);
    const result = await fetchPromise;
    return result;
  }

  static async getProfile(userId: string): Promise<any> {
    const cacheKey = this.getCacheKey(userId, "profile");
    const cached = userCache.get(cacheKey);

    if (cached && cached.profile && this.isCacheValid(cached.timestamp)) {
      return cached.profile;
    }

    // Check if request is already pending
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey);
    }

    const fetchPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", userId)
          .single();

        if (error) throw error;

        // Update cache
        const existing = userCache.get(cacheKey) || emptyCacheEntry();
        userCache.set(cacheKey, {
          ...existing,
          profile: data,
          timestamp: Date.now(),
        });

        return data;
      } finally {
        pendingRequests.delete(cacheKey);
      }
    })();

    pendingRequests.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  static async getUserRole(userId: string): Promise<string | null> {
    const cacheKey = this.getCacheKey(userId, "role");
    const cached = userCache.get(cacheKey);

    if (cached && cached.role && this.isCacheValid(cached.timestamp)) {
      return cached.role;
    }

    // Check if request is already pending
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey);
    }

    const fetchPromise = (async () => {
      try {
        // Try metadata first
        const user = await this.getUser(userId);
        if (user?.user_metadata?.role) {
          const role = user.user_metadata.role;

          // Update cache
          const existing = userCache.get(cacheKey) || emptyCacheEntry();
          userCache.set(cacheKey, {
            ...existing,
            role,
            timestamp: Date.now(),
          });

          return role;
        }

        // Fallback to profiles table
        const { data, error } = await supabase
          .from("profiles")
          .select("role")
          .eq("user_id", userId)
          .single();

        if (error) throw error;

        // Default-DENY: a null/missing role is NOT silently treated as "seller" (that was a
        // client-side privilege grant). Unknown role ⇒ null ⇒ empty capability set downstream.
        const role = data?.role ?? null;

        // Update cache
        const existing = userCache.get(cacheKey) || emptyCacheEntry();
        userCache.set(cacheKey, {
          ...existing,
          role,
          timestamp: Date.now(),
        });

        return role;
      } finally {
        pendingRequests.delete(cacheKey);
      }
    })();

    pendingRequests.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  /**
   * Resolve the caller's org position (profiles.org_role) for L3 capability checks. Cached 60s.
   * Default-DENY: a missing org_role ⇒ null ⇒ no org-position capabilities downstream.
   */
  static async getOrgRole(userId: string): Promise<string | null> {
    const cacheKey = this.getCacheKey(userId, "orgRole");
    const cached = userCache.get(cacheKey);
    if (cached && this.isCacheValid(cached.timestamp)) {
      return cached.orgRole ?? null;
    }
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey);
    }

    const fetchPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("org_role")
          .eq("user_id", userId)
          .single();
        if (error) throw error;
        const orgRole = data?.org_role ?? null;
        userCache.set(cacheKey, { ...emptyCacheEntry(), orgRole, timestamp: Date.now() });
        return orgRole;
      } finally {
        pendingRequests.delete(cacheKey);
      }
    })();

    pendingRequests.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  /**
   * The role fields for a user, read SYNCHRONOUSLY, or null if not all three are
   * cached and fresh.
   *
   * Exists so `useCapabilities` can be resolved on its very first render for a
   * returning user. Its three lookups are `async` even on a cache hit, so a
   * freshly-mounted guard always rendered unresolved for at least one commit —
   * which meant a full 300px Zen mark and a 520ms dissolve on every navigation
   * that crossed a guard boundary (/tracks → /upload swaps SellerGuard for
   * NonNegotiatorGuard at the same route position, so React remounts it), for
   * what is a sub-millisecond read from a Map.
   *
   * Freshness is INHERITED, never invented: this reads the same entries, with the
   * same `isCacheValid` window, that the async accessors write. It is therefore
   * never fresher than they are, and `clearCache` — already called by AuthProvider
   * on sign-out and on a same-tab account switch, before the new identity is
   * committed — invalidates it for free.
   */
  static getResolvedRolesSync(userId: string): {
    accountType: string | null;
    orgRole: string | null;
    organizationId: string | null;
  } | null {
    const role = userCache.get(this.getCacheKey(userId, "role"));
    const orgRole = userCache.get(this.getCacheKey(userId, "orgRole"));
    const profile = userCache.get(this.getCacheKey(userId, "profile"));

    // All three must be present AND fresh. A partial hit would resolve the guard
    // against an incomplete subject, which is the flash this hook exists to stop.
    if (!role || !this.isCacheValid(role.timestamp)) return null;
    if (!orgRole || !this.isCacheValid(orgRole.timestamp)) return null;
    if (!profile || !profile.profile || !this.isCacheValid(profile.timestamp)) return null;

    return {
      accountType: role.role ?? null,
      orgRole: orgRole.orgRole ?? null,
      organizationId: profile.profile.organization_id ?? null,
    };
  }

  /**
   * The cached profile, read SYNCHRONOUSLY, or null when none is cached and fresh. Same entry and
   * same freshness window as `getProfile`, so it is never fresher than that. It lets
   * `useViewerAccess` answer on its first render for an account whose profile is already loaded,
   * instead of showing a loader for a read that is a Map lookup.
   */
  static getCachedProfileSync(userId: string): any | null {
    const cached = userCache.get(this.getCacheKey(userId, "profile"));
    if (!cached || !cached.profile || !this.isCacheValid(cached.timestamp)) return null;
    return cached.profile;
  }

  static clearCache(userId?: string): void {
    if (userId) {
      // Clear specific user's cache
      const keys = Array.from(userCache.keys()).filter((key) =>
        key.startsWith(userId)
      );
      keys.forEach((key) => userCache.delete(key));
      // Also drop any in-flight fetches for this user so a late resolve can't repopulate the cache
      // after a reset (the cross-user leak hazard on account switch).
      Array.from(pendingRequests.keys())
        .filter((key) => key.startsWith(userId))
        .forEach((key) => pendingRequests.delete(key));
    } else {
      // Clear all cache (incl. the unified "auth-user" entry) + all in-flight requests
      userCache.clear();
      pendingRequests.clear();
    }
    // Silent in production; consider hooking into a debug logger if needed
  }

  static clearProfileCache(userId: string): void {
    const cacheKey = this.getCacheKey(userId, "profile");
    userCache.delete(cacheKey);
    pendingRequests.delete(cacheKey);
  }

  static async getProfilesBatch(userIds: string[]): Promise<Map<string, any>> {
    const profilesMap = new Map<string, any>();
    const userIdsToFetch: string[] = [];
    const now = Date.now();

    // Deduplicate IDs and check cache
    const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
    
    for (const userId of uniqueUserIds) {
      const cacheKey = this.getCacheKey(userId, "profile");
      const cached = userCache.get(cacheKey);

      if (cached && cached.profile && this.isCacheValid(cached.timestamp)) {
        profilesMap.set(userId, cached.profile);
      } else if (pendingRequests.has(cacheKey)) {
        try {
          const profile = await pendingRequests.get(cacheKey);
          profilesMap.set(userId, profile);
        } catch (error) {
          userIdsToFetch.push(userId);
        }
      } else {
        userIdsToFetch.push(userId);
      }
    }

    if (userIdsToFetch.length > 0) {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("user_id, display_name, *")
          .in("user_id", userIdsToFetch);

        if (error) throw error;

        if (data) {
          for (const profile of data) {
            const userId = profile.user_id;
            profilesMap.set(userId, profile);

            const cacheKey = this.getCacheKey(userId, "profile");
            const existing = userCache.get(cacheKey) || emptyCacheEntry();
            userCache.set(cacheKey, {
              ...existing,
              profile,
              timestamp: now,
            });
          }
        }
      } catch (error) {
        console.error("Failed to batch fetch profiles:", error);
        // Silently fail - individual mappers can handle missing profiles
      }
    }

    return profilesMap;
  }

  static getCacheStats(): { size: number; keys: string[] } {
    return {
      size: userCache.size,
      keys: Array.from(userCache.keys()),
    };
  }
}
